import React, { useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  getDataOnRequest,
  submitDataOnRequest,
  errorToast,
  successToast,
} from "../../../redux/actions/CommonActions";
import Label from "../../Layout/Label";
import { format } from "date-fns";
import { optionsArray, optionsArrayEndTime } from "./TimeOptions";
import composedAvaibilityContainer from "../../../containers/AvaibilityContainer";
import {
  getCurrentWeekDetails,
} from "../../../services/GlobalCurrentDateAndTime";
import swal from "sweetalert";

const createSlot = (day, date, status = "available", inTime = "08:00", outTime = "17:00") => ({
  cnd_availability_week_id: "",
  cnd_availability_week_date: date,
  cnd_availability_week_day: day,
  cnd_availability_week_in_time: inTime,
  cnd_availability_week_out_time: outTime,
  cnd_availability_week_status: status,
  booked_assignments: [],
});

const mergeSlots = (slots) => {
  if (!slots || slots.length <= 1) return slots;
  const available = slots.filter(s => s.cnd_availability_week_status === "available");
  const nonAvailable = slots.filter(s => s.cnd_availability_week_status !== "available");
  available.sort((a, b) => a.cnd_availability_week_in_time.localeCompare(b.cnd_availability_week_in_time));
  const merged = [];
  for (const slot of available) {
    if (slot.cnd_availability_week_in_time === slot.cnd_availability_week_out_time) continue;
    if (slot.booked_assignments && slot.booked_assignments.length > 0) {
      merged.push({ ...slot });
      continue;
    }
    if (merged.length === 0) {
      merged.push({ ...slot });
    } else {
      const last = merged[merged.length - 1];
      if (last.cnd_availability_week_out_time === slot.cnd_availability_week_in_time) {
        last.cnd_availability_week_out_time = slot.cnd_availability_week_out_time;
      } else {
        merged.push({ ...slot });
      }
    }
  }
  return [...nonAvailable, ...merged];
};

const setLoader = (show) => {
  const el = document.getElementById("loader");
  if (el) el.style.display = show ? "block" : "none";
};

const MobileAvailabilityForm = (props) => {
  const [checkRowDateCondition, setCheckRowDateCondtion] = useState(false);
  const { startDate } = getCurrentWeekDetails();
  const [dateSelected, setDateSelected] = useState(startDate);

  const { userDetails, avaibility } = props;
  const dispatch = useDispatch();
  const weekDays = [
    "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  ];

  const isWeekday = () => {
    let date = new Date();
    let sub = date.getDay() > 0 ? 1 : -6;
    let monday = new Date(date.setDate(date.getDate() - date.getDay() + sub));
    return monday.toISOString().split("T")[0];
  };

  const validationSchema = Yup.object().shape({
    cnd_avail_date_from_date: Yup.string()
      .required("Date is required")
      .matches(
        /^(19|20)\d\d[- /.](0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])$/,
        "Please select the date from calendar in YYYY-MM-DD format!"
      ),
  });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const [availabilityData, setAvailabilityData] = useState(null);
  const [formattedData, setFormattedData] = useState(null);
  const history = useHistory();

  const formatDate = (d1) => {
    if (!d1) return "";
    let date = new Date(d1);
    if (isNaN(date.getTime())) return "";
    let mnth = ("0" + (date.getMonth() + 1)).slice(-2);
    let day = ("0" + date.getDate()).slice(-2);
    return [date.getFullYear(), mnth, day].join("-");
  };

  const computeWeekRange = (dateStr) => {
    let d = new Date(dateStr);
    if (isNaN(d.getTime())) d = new Date();
    let year = d.getFullYear();
    let firstJan = new Date(year, 0, 1);
    let days = Math.floor((d - firstJan) / (24 * 60 * 60 * 1000));
    let weekNum = Math.ceil((d.getDay() + 1 + days) / 7);
    let weekLabel = year + "-W" + String(weekNum).padStart(2, "0");
    let firstDayOfYear = new Date(year, 0, 1);
    let firstDayOfWeek = firstDayOfYear.getDay() - 1;
    let firstWeekStart = new Date(year, 0, 1 - firstDayOfWeek);
    let weekStart = new Date(firstWeekStart.getTime() + weekNum * 7 * 24 * 60 * 60 * 1000);
    let weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
    return { weekStart: formatDate(weekStart), weekEnd: formatDate(weekEnd), weekLabel };
  };

  const getDatesBetween = (start, end) => {
    const arr = [];
    let current = new Date(start);
    const endDate = new Date(end);
    while (current <= endDate) {
      arr.push(formatDate(current));
      current.setDate(current.getDate() + 1);
    }
    return arr;
  };

  const buildDefaultWeekData = (dateStr) => {
    if (!dateStr) dateStr = isWeekday();
    const { weekStart, weekEnd } = computeWeekRange(dateStr);
    const dateList = getDatesBetween(weekStart, weekEnd);
    let weekObj = {};
    dateList.forEach((d, idx) => {
      if (idx < weekDays.length) {
        weekObj[weekDays[idx]] = [createSlot(weekDays[idx], d, "all_day", "00:00", "00:00")];
      }
    });
    return { weekObj, weekStart, weekEnd, weekLabel: computeWeekRange(dateStr).weekLabel };
  };

  const initWeek = (dateStr) => {
    const { weekObj, weekStart, weekEnd, weekLabel } = buildDefaultWeekData(dateStr);
    setValue("cnd_avail_date_from_date", weekStart);
    setValue("cnd_avail_date_to_date", weekEnd);
    setValue("cnd_avail_week", weekLabel);
    setDateSelected(weekStart);
    applyApiData(weekObj);
  };

  const applyApiData = (weekObj) => {
    let result = { ...weekObj };
    if (avaibility && avaibility.weekly_availability && avaibility.weekly_availability.length > 0) {
      const first = avaibility.weekly_availability[0];
      if (!first) return;
      const apiData = Array.isArray(first) ? first[0] : first;
      if (apiData && typeof apiData === "object") {
        weekDays.forEach(day => {
          const apiSlots = apiData[day];
          if (Array.isArray(apiSlots) && apiSlots.length > 0) {
            const apiDate = apiSlots[0].cnd_availability_week_date;
            const localDate = result[day]?.[0]?.cnd_availability_week_date;
            if (apiDate && localDate && apiDate === localDate) {
              result[day] = apiSlots.map(s => ({
                ...s,
                cnd_availability_week_copy_status: s.cnd_availability_week_copy_status || "",
              }));
            }
          }
        });
      }
    }
    const weekArray = [result];
    setAvailabilityData(weekArray);
    setFormattedData(weekArray);
    setValue("avaibility", weekArray);
  };

  useEffect(() => {
    if (getValues("cnd_avail_date_from_date")) {
      initWeek(getValues("cnd_avail_date_from_date"));
    } else if (avaibility && avaibility.cnd_avail_date_from_date) {
      initWeek(avaibility.cnd_avail_date_from_date);
    } else {
      initWeek(isWeekday());
    }
  }, [avaibility]);

  const refreshFromApi = (dateStr) => {
    if (!dateStr) return;
    setLoader(true);
    setDateSelected(dateStr);
    dispatch(
      getDataOnRequest(
        props.userAuth.users_access_token + "/" + dateStr,
        "GetAvaibilityStatus",
        "SET_AVAIBILITY"
      )
    ).finally(() => {
      setLoader(false);
    });
  };

  const changeWeek = (direction) => {
    let current = getValues("cnd_avail_date_from_date") || isWeekday();
    let d = new Date(current);
    if (isNaN(d.getTime())) d = new Date();
    d.setDate(d.getDate() + (direction === "next" ? 7 : -7));
    let newDate = formatDate(d);
    if (newDate) {
      setValue("cnd_avail_date_from_date", newDate);
      refreshFromApi(newDate);
      initWeek(newDate);
    }
  };

  const onChangeStartTime = (e, day, weekIdx, slotIdx) => {
    if (!availabilityData || !availabilityData[weekIdx]) return;
    let copy = JSON.parse(JSON.stringify(availabilityData));
    if (!copy[weekIdx][day] || !copy[weekIdx][day][slotIdx]) return;
    copy[weekIdx][day][slotIdx].cnd_availability_week_in_time = e.target.value;
    if (copy[weekIdx][day][slotIdx].cnd_availability_week_status === "all_day") {
      copy[weekIdx][day][slotIdx].cnd_availability_week_status = "available";
    }
    setAvailabilityData(copy);
    setFormattedData(copy);
  };

  const onChangeEndTime = (e, day, weekIdx, slotIdx) => {
    if (!availabilityData || !availabilityData[weekIdx]) return;
    let copy = JSON.parse(JSON.stringify(availabilityData));
    if (!copy[weekIdx][day] || !copy[weekIdx][day][slotIdx]) return;
    copy[weekIdx][day][slotIdx].cnd_availability_week_out_time = e.target.value;
    if (copy[weekIdx][day][slotIdx].cnd_availability_week_status === "all_day") {
      copy[weekIdx][day][slotIdx].cnd_availability_week_status = "available";
    }
    setAvailabilityData(copy);
    setFormattedData(copy);
  };

  const onChangeStatusAll = (e, day, weekIdx) => {
    if (!availabilityData || !availabilityData[weekIdx]) return;
    const daySlots = availabilityData[weekIdx][day] || [];
    if (daySlots.some(s => s.booked_assignments?.length > 0)) {
      swal("Booking Exists", "You can't make it full day as the slot has booked assignments.", "warning");
      e.preventDefault();
      return;
    }
    let copy = JSON.parse(JSON.stringify(availabilityData));
    const date = copy[weekIdx][day]?.[0]?.cnd_availability_week_date || "";
    if (e.target.checked) {
      copy[weekIdx][day] = [createSlot(day, date, "all_day", "00:00", "00:00")];
    } else {
      copy[weekIdx][day] = [createSlot(day, date, "available", "08:00", "17:00")];
    }
    setAvailabilityData(copy);
    setFormattedData(copy);
  };

  const onChangeStatusUn = (e, day, weekIdx) => {
    if (!availabilityData || !availabilityData[weekIdx]) return;
    const daySlots = availabilityData[weekIdx][day] || [];
    if (daySlots.some(s => s.booked_assignments?.length > 0)) {
      swal("Booking Exists", "You can't make it unavailable as the slot has booked assignments.", "warning");
      e.preventDefault();
      return;
    }
    let copy = JSON.parse(JSON.stringify(availabilityData));
    const date = copy[weekIdx][day]?.[0]?.cnd_availability_week_date || "";
    if (e.target.checked) {
      copy[weekIdx][day] = [createSlot(day, date, "unavailable", "00:00", "00:00")];
    } else {
      copy[weekIdx][day] = [createSlot(day, date, "all_day", "00:00", "00:00")];
    }
    setAvailabilityData(copy);
    setFormattedData(copy);
  };

  const addSlot = (day, weekIdx) => {
    if (!availabilityData || !availabilityData[weekIdx]) return;
    let copy = JSON.parse(JSON.stringify(availabilityData));
    const currentSlots = copy[weekIdx][day] || [];
    const date = currentSlots[0]?.cnd_availability_week_date || "";
    let newInTime = "09:00";
    if (currentSlots.length > 0) {
      const last = currentSlots[currentSlots.length - 1];
      if (last.cnd_availability_week_status === "available" && last.cnd_availability_week_out_time !== "00:00") {
        newInTime = last.cnd_availability_week_out_time;
      }
    }
    let newOutTime = "17:00";
    const idx = optionsArrayEndTime.findIndex(o => o.value > newInTime);
    if (idx !== -1) newOutTime = optionsArrayEndTime[idx].value;
    copy[weekIdx][day] = [...currentSlots, createSlot(day, date, "available", newInTime, newOutTime)];
    setAvailabilityData(copy);
    setFormattedData(copy);
  };

  const removeSlot = (day, weekIdx, slotIdx) => {
    if (!availabilityData || !availabilityData[weekIdx]) return;
    let copy = JSON.parse(JSON.stringify(availabilityData));
    const currentSlots = copy[weekIdx][day] || [];
    if (currentSlots.length <= 1) return;
    copy[weekIdx][day] = currentSlots.filter((_, i) => i !== slotIdx);
    setAvailabilityData(copy);
    setFormattedData(copy);
  };

  const handleSave = () => {
    let dateStr = getValues("cnd_avail_date_from_date") || isWeekday();
    const { weekStart, weekEnd, weekLabel } = computeWeekRange(dateStr);
    const merged = (availabilityData || []).map(week => {
      let m = {};
      weekDays.forEach(day => {
        if (week[day]) m[day] = mergeSlots(week[day]);
      });
      return m;
    });
    let payload = {
      users_access_token: userDetails.users_access_token,
      cnd_avail_week: weekLabel,
      cnd_avail_date_from_date: weekStart,
      cnd_avail_date_to_date: weekEnd,
      cnd_availability_week_copy: getValues("cnd_availability_week_copy") || "",
      weekly_availability: merged,
    };
    setLoader(true);
    dispatch(
      submitDataOnRequest(JSON.stringify(payload), "SaveAvaibilityStatus", "", [
        getDataOnRequest(
          props.userAuth.users_access_token + "/" + dateSelected,
          "GetAvaibilityStatus",
          "SET_AVAIBILITY"
        ),
      ])
    )
      .then((res) => {
        setLoader(false);
        if (res && res.status === "success") {
          successToast(res.msg || "Availability saved successfully");
        } else {
          errorToast(res?.msg || "Failed to save availability");
        }
      })
      .catch((err) => {
        setLoader(false);
        errorToast(err.message || "Something went wrong");
      });
  };

  useEffect(() => {
    if (formattedData && formattedData[0]) {
      const lastDate = formattedData[0]?.sunday?.[0]?.cnd_availability_week_date;
      const today = formatDate(new Date());
      setCheckRowDateCondtion(lastDate ? lastDate >= today : false);
    }
  }, [formattedData]);

  const canEdit = (dateStr) => {
    return dateStr >= formatDate(new Date());
  };

  const checkCopyStatus = formattedData?.[0]?.monday?.[0]?.cnd_availability_week_copy_status;
  const weekFormatted = formattedData && formattedData[0];
  const hasExistingId = weekFormatted && weekDays.some(day =>
    weekFormatted[day]?.some(slot => slot.cnd_availability_week_id)
  );

  return (
    <React.Fragment>
      <div className="job-bx table-job-bx clearfix">
        <div className="job-bx-title clearfix mb-4">
          <h5 className="font-weight-700 text-uppercase mb-0" style={{ color: "#2e3192", fontSize: "18px", letterSpacing: "0.5px" }}>
            <i className="fa fa-calendar-alt me-2"></i>Weekly Availability
          </h5>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-4 p-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
          <div style={{ flex: 1, minWidth: "160px" }}>
            <Label labelName="Week Commencing" />
            <Form.Control
              type="date"
              name="cnd_avail_date_from_date"
              min={isWeekday()}
              step={7}
              size="sm"
              {...register("cnd_avail_date_from_date")}
              isInvalid={!!errors?.cnd_avail_date_from_date}
              onChange={(e) => {
                if (e.target.value) {
                  refreshFromApi(e.target.value);
                  initWeek(e.target.value);
                }
              }}
              style={{ borderRadius: "8px", borderColor: "#e2e8f0" }}
            />
          </div>
          <div className="d-flex align-items-center gap-2" style={{ paddingTop: "22px" }}>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => changeWeek("prev")}
              style={{ borderRadius: "8px", width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="fa fa-angle-left"></i>
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => changeWeek("next")}
              style={{ borderRadius: "8px", width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="fa fa-angle-right"></i>
            </button>
          </div>
        </div>

        {!weekFormatted ? (
          <div className="text-center py-5 text-muted">Loading availability data...</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end", marginBottom: "4px" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "3px", backgroundColor: "#34bfa3", display: "inline-block" }}></span> Available
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "3px", backgroundColor: "#2e3192", display: "inline-block" }}></span> Full Day
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "3px", backgroundColor: "#e74c5e", display: "inline-block" }}></span> Unavailable
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "3px", backgroundColor: "#8b5cf6", display: "inline-block" }}></span> Booked
              </span>
            </div>
            {weekDays.map((day, idx) => {
              const slots = weekFormatted[day] || [];
              const dayStatus = slots[0]?.cnd_availability_week_status || "all_day";
              const isAllDay = dayStatus === "all_day";
              const isUnavailable = dayStatus === "unavailable";
              const editable = canEdit(slots[0]?.cnd_availability_week_date);
              const hasBookings = slots.some(s => s.booked_assignments?.length > 0);

              const headerBg = isAllDay ? "#eef2ff" : isUnavailable ? "#fff1f2" : "#f1f5f9";
              const statusColor = hasBookings ? "#8b5cf6" : isUnavailable ? "#e74c5e" : "#34bfa3";

              return (
                <div className="col-12" key={day}>
                  <div style={{ borderRadius: "8px", border: "1px solid #e9ecef", overflow: "hidden", borderLeft: `4px solid ${statusColor}` }}>
                    <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ backgroundColor: headerBg, borderBottom: "1px solid #dee2e6" }}>
                      <div>
                        <span className="fw-bold text-capitalize" style={{ fontSize: "14px", color: "#495057" }}>{day}</span>
                        <span className="ms-2 text-muted" style={{ fontSize: "12px" }}>
                          {slots[0] && format(new Date(slots[0].cnd_availability_week_date), "dd MMM yyyy")}
                        </span>
                      </div>
                      <div className="d-flex gap-3">
                        <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
                          <input type="checkbox" checked={isAllDay} onChange={(e) => onChangeStatusAll(e, day, 0)} disabled={!editable || isAllDay} style={{ width: "16px", height: "16px", cursor: isAllDay || hasBookings ? "not-allowed" : "pointer" }} />
                          <span style={{ color: isAllDay ? "#2e3192" : "#64748b", fontWeight: isAllDay ? "700" : "500" }}>Full Day</span>
                        </label>
                        <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px", cursor: hasBookings ? "not-allowed" : "pointer" }}>
                          <input type="checkbox" checked={isUnavailable} onChange={(e) => onChangeStatusUn(e, day, 0)} disabled={!editable} style={{ width: "16px", height: "16px", cursor: hasBookings ? "not-allowed" : "pointer" }} />
                          <span style={{ color: isUnavailable ? "#e74c5e" : "#64748b", fontWeight: isUnavailable ? "700" : "500" }}>Unavailable</span>
                        </label>
                      </div>
                    </div>

                    <div className="p-3">
                      {isUnavailable && (
                        <div className="text-center py-3">
                          <span className="badge" style={{ backgroundColor: "#e74c5e", fontSize: "13px", padding: "6px 16px", borderRadius: "20px" }}>Unavailable</span>
                        </div>
                      )}

                      {isAllDay && (
                        <div>
                          <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                            <Form.Control as="select" size="sm" value="00:00" disabled={!editable}
                              onChange={(e) => onChangeStartTime(e, day, 0, 0)}
                              style={{ fontSize: "14px", padding: "8px 12px", height: "40px", width: "110px", borderRadius: "8px", border: "1.5px solid #d1d5db", backgroundColor: "#fff", fontWeight: 600, color: "#1e293b" }}>
                              {optionsArray.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                            </Form.Control>
                            <span style={{ fontSize: "15px", color: "#94a3b8", fontWeight: 700 }}>to</span>
                            <Form.Control as="select" size="sm" value="00:00" disabled={!editable}
                              onChange={(e) => onChangeEndTime(e, day, 0, 0)}
                              style={{ fontSize: "14px", padding: "8px 12px", height: "40px", width: "110px", borderRadius: "8px", border: "1.5px solid #d1d5db", backgroundColor: "#fff", fontWeight: 600, color: "#1e293b" }}>
                              {optionsArrayEndTime.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                            </Form.Control>
                          </div>
                          <div className="text-center">
                            <span className="badge" style={{ backgroundColor: "#2e3192", fontSize: "12px", padding: "5px 16px", borderRadius: "20px" }}>Full Day</span>
                          </div>
                        </div>
                      )}

                      {!isAllDay && !isUnavailable && (
                        <div>
                          {slots.map((slot, si) => (
                            <div key={si} style={{ backgroundColor: "#f8fafc", borderRadius: "8px", padding: "10px 12px", border: "1px solid #e2e8f0", marginBottom: "6px" }}>
                              <div className="d-flex align-items-center gap-2">
                                <Form.Control as="select" size="sm" value={slot.cnd_availability_week_in_time || "00:00"}
                                  onChange={(e) => onChangeStartTime(e, day, 0, si)} disabled={!editable}
                                  style={{ fontSize: "14px", padding: "8px 12px", height: "38px", width: "100px", borderRadius: "8px", border: "1.5px solid #d1d5db", backgroundColor: "#fff" }}>
                                  {optionsArray.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                                </Form.Control>
                                <span style={{ fontSize: "14px", color: "#94a3b8", fontWeight: 700 }}>to</span>
                                <Form.Control as="select" size="sm" value={slot.cnd_availability_week_out_time !== "23:59" ? slot.cnd_availability_week_out_time : "00:00"}
                                  onChange={(e) => onChangeEndTime(e, day, 0, si)} disabled={!editable}
                                  style={{ fontSize: "14px", padding: "8px 12px", height: "38px", width: "100px", borderRadius: "8px", border: "1.5px solid #d1d5db", backgroundColor: "#fff" }}>
                                  {optionsArrayEndTime.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                                </Form.Control>
                                {slots.length > 1 && editable && (
                                  <button type="button" onClick={() => removeSlot(day, 0, si)}
                                    style={{ background: "none", border: "none", color: "#94a3b8", fontSize: "18px", padding: "0 4px", cursor: "pointer", lineHeight: 1 }}
                                    onMouseEnter={(e) => e.target.style.color = "#e74c5e"}
                                    onMouseLeave={(e) => e.target.style.color = "#94a3b8"}
                                    title="Remove slot">
                                    <i className="fa fa-times-circle"></i>
                                  </button>
                                )}
                              </div>
                              {slot.booked_assignments && slot.booked_assignments.length > 0 && slot.booked_assignments.map((a, ai) => (
                                <div key={ai} style={{ fontSize: "12px", backgroundColor: (a.clt_color_code || "#8b5cf6") + "15", borderLeft: `3px solid ${a.cnd_assignment_client_type === "domiciliary_care" ? "#6b7280" : a.clt_color_code || "#8b5cf6"}`, borderRadius: "4px", padding: "5px 8px", marginTop: "6px" }}>
                                  <div className="fw-bold" style={{ fontSize: "12px", color: "#1e293b" }}>{a.client_name || a.clt_company_name}</div>
                                  <div style={{ fontSize: "11px", color: "#64748b" }}>
                                    {a.cnd_assignment_start_time?.substring(0, 5)} – {a.cnd_assignment_end_time?.substring(0, 5)}
                                    {a.job_title && <span className="ms-2 text-muted">| {a.job_title}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                          {editable && (
                            <button type="button" onClick={() => addSlot(day, 0)}
                              style={{ width: "100%", marginTop: "4px", padding: "8px 0", fontSize: "13px", fontWeight: 500, backgroundColor: "transparent", border: "1px dashed #cbd5e1", borderRadius: "8px", color: "#64748b", cursor: "pointer", transition: "all 0.2s ease" }}
                              onMouseEnter={(e) => { e.target.style.borderColor = "#34bfa3"; e.target.style.color = "#34bfa3"; e.target.style.backgroundColor = "#f0fdfa"; }}
                              onMouseLeave={(e) => { e.target.style.borderColor = "#cbd5e1"; e.target.style.color = "#64748b"; e.target.style.backgroundColor = "transparent"; }}>
                              <i className="fa fa-plus me-1"></i>Add Time Slot
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="row mt-4 align-items-end">
          <div className="col-12 mb-3">
            <Label labelName={!checkCopyStatus && checkRowDateCondition && !hasExistingId ? "Availability to be repeated in weeks" : ""} req={false} />
            {!checkCopyStatus && checkRowDateCondition && !hasExistingId && (
              <Form.Control as="select" size="sm" style={{ maxWidth: "200px" }} {...register("cnd_availability_week_copy")}>
                <option value="">Select Weeks</option>
                <option value="1">1 Week</option>
                <option value="2">2 Weeks</option>
                <option value="3">3 Weeks</option>
                <option value="4">4 Weeks</option>
              </Form.Control>
            )}
          </div>
          <div className="col-12 text-center">
            {checkRowDateCondition && (
              <button onClick={handleSave} className="site-button green" style={{ padding: "12px 40px", fontWeight: 600, fontSize: "14px", borderRadius: "8px", letterSpacing: "0.3px" }}>
                <i className="fa fa-save me-2"></i>
                {!checkCopyStatus ? "Save Availability" : "Update Availability"}
              </button>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  userDetails: state.auth.authUserData,
  avaibility: state.avaibility.avaibility,
});

export default connect(mapStateToProps)(
  composedAvaibilityContainer(MobileAvailabilityForm)
);
